/**
 * Scene Service - Sprint 15-16
 * CRUD operations for multi-scene projects and individual scenes
 * 
 * @example
 * ```typescript
 * // Create a new scene
 * const scene = await SceneService.createScene({
 *   order: 0,
 *   title: 'Introduction',
 *   duration: 0,
 *   videoChunks: [],
 *   layout: LayoutPreset.FULL_SCREEN,
 *   background: { type: 'solid', colors: ['#1890ff'] },
 *   status: 'pending'
 * });
 * 
 * // Create a multi-scene project
 * const project = await SceneService.createProject({
 *   title: 'My Video Project',
 *   scenes: [scene1, scene2, scene3],
 *   totalDuration: 120000,
 *   status: 'draft'
 * });
 * ```
 */

import { db } from '../idb/chunk-db';
import { 
  Scene, 
  MultiSceneProject, 
  SceneStatus, 
  ProjectStatus,
  SceneChunkMapping 
} from '../../../types/scene';
import { VideoThumbnailService } from './videoThumbnailService';
import { SceneTagType } from '../../../types/template';
import logger from '../utils/logger';
import { getDefaultTransition } from '../constants/transitionConstants';
import { projectsManager } from './projectsManager';
import { getSupportedMimeType, BITRATES_BY_QUALITY, AUDIO_BITRATE } from '../utils/browserCompatibility';

const generateId = () => crypto.randomUUID();

export class SceneService {
  
  // ==================== Scene CRUD Operations ====================
  
  /**
   * Create a new scene in the database
   * @param sceneData - Scene data without id (id is auto-generated)
   * @returns The created scene with generated id
   */
  static async createScene(sceneData: Omit<Scene, 'id'>): Promise<Scene> {
    const scene: Scene = {
      id: generateId(),
      ...sceneData,
    };
    
    await db.scenes.add(scene);
    logger.info('[SceneService] Scene created:', scene.id);
    return scene;
  }
  
  static async getSceneById(sceneId: string, projectId?: string): Promise<Scene | undefined> {
    // Check if this is a cloud project
    const isCloudProject = projectId && !isNaN(Number(projectId));
    
    if (isCloudProject) {
      try {
        logger.info('[SceneService] Fetching scene from PostgreSQL:', { sceneId, projectId });
        const { projectsManager } = await import('./projectsManager');
        
        // Get full project data (includes scenes)
        const project = await projectsManager.getProject(Number(projectId));
        
        logger.info('[SceneService] Project data received:', {
          projectId: project.id,
          hasScenes: !!project.scenes,
          sceneCount: project.scenes?.length || 0
        });
        
        // Find the specific scene in the project's scenes array
        const scene = project.scenes?.find((s: any) => String(s.id) === sceneId || s.id === Number(sceneId));
        
        if (scene) {
          // Extract transitionOut from metadata if not already on scene
          const transitionOut = scene.transitionOut || scene.metadata?.transitionOut;
          
          logger.info('[SceneService] ✅ Cloud scene fetched from PostgreSQL:', {
            sceneId: scene.id,
            title: scene.title,
            status: scene.status,
            videoUrl: scene.videoUrl,
            transitionOut: transitionOut
          });
          
          // Return as Scene type with extracted transitionOut
          return {
            ...scene,
            transitionOut
          } as any as Scene;
        }
        
        logger.warn('[SceneService] Scene not found in project scenes:', { sceneId, projectId });
        return undefined;
      } catch (error) {
        logger.error('[SceneService] Failed to fetch cloud scene:', error);
        throw error;
      }
    } else {
      // For local projects, get from IndexedDB
      return await db.scenes.get(sceneId);
    }
  }
  
  static async getAllScenes(): Promise<Scene[]> {
    return await db.scenes.toArray();
  }
  
  static async getScenesByStatus(status: SceneStatus): Promise<Scene[]> {
    return await db.scenes.where('status').equals(status).toArray();
  }
  
  static async updateScene(sceneId: string, updates: Partial<Scene>, projectId?: string): Promise<void> {
    // Check if this is a cloud project
    const isCloudProject = projectId && !isNaN(Number(projectId));
    
    console.log('🔧 [SceneService.updateScene] Called with:', {
      sceneId,
      projectId,
      isCloudProject,
      updates: Object.keys(updates)
    });
    
    if (isCloudProject) {
      // For cloud projects, update scene in PostgreSQL
      try {
        const { projectsManager } = await import('./projectsManager');
        const updatePayload: any = {};
        
        // Direct database fields
        if (updates.duration !== undefined) updatePayload.duration = updates.duration;
        if (updates.tags !== undefined) updatePayload.tags = updates.tags;
        if (updates.title !== undefined) updatePayload.title = updates.title;
        if (updates.status !== undefined) updatePayload.status = updates.status;
        if (updates.videoUrl !== undefined) updatePayload.videoUrl = updates.videoUrl;
        if (updates.thumbnailUrl !== undefined) updatePayload.thumbnailUrl = updates.thumbnailUrl;
        if (updates.videoGuid !== undefined) updatePayload.videoGuid = updates.videoGuid;
        if (updates.recordedAt !== undefined) updatePayload.recordedAt = updates.recordedAt;
        if (updates.micEnabled !== undefined) updatePayload.micEnabled = updates.micEnabled;
        if (updates.analysisStatus !== undefined) updatePayload.analysisStatus = updates.analysisStatus;
        
        // Metadata fields (stored in JSONB metadata column)
        const metadataFields = ['layout', 'background', 'script', 'layoutPreset', 'transitionOut', 'notes'];
        const hasMetadataUpdates = metadataFields.some(field => updates[field as keyof Scene] !== undefined);
        
        if (hasMetadataUpdates && projectId) {
          // First check IndexedDB for pending_upload scenes (they might not be in PostgreSQL yet)
          const localScene = await db.scenes.get(sceneId);
          let currentMetadata = {};
          
          if (localScene && localScene.status === 'pending_upload') {
            // Scene is pending upload - use local metadata from IndexedDB
            console.log('[SceneService] 🔍 Using IndexedDB metadata for pending_upload scene:', sceneId);
            currentMetadata = {
              layout: localScene.layout,
              background: localScene.background,
              script: localScene.script,
              layoutPreset: localScene.layoutPreset,
              transitionOut: localScene.transitionOut,
              notes: localScene.notes,
            };
          } else {
            // Fetch current scene metadata from PostgreSQL
            const numericProjectId = Number(projectId);
            const response = await projectsManager.getProject(numericProjectId);
            const { scenes } = response as any;
            
            // Find the current scene
            const currentScene = scenes.find((s: any) => s.id === Number(sceneId));
            currentMetadata = currentScene?.metadata || {};
          }
          
          // Merge metadata updates
          const updatedMetadata = { ...currentMetadata };
          if (updates.layout !== undefined) updatedMetadata.layout = updates.layout;
          if (updates.background !== undefined) updatedMetadata.background = updates.background;
          if (updates.script !== undefined) updatedMetadata.script = updates.script;
          if (updates.layoutPreset !== undefined) updatedMetadata.layoutPreset = updates.layoutPreset;
          if (updates.transitionOut !== undefined) updatedMetadata.transitionOut = updates.transitionOut;
          if (updates.notes !== undefined) updatedMetadata.notes = updates.notes;
          
          updatePayload.metadata = updatedMetadata;
          
          console.log('[SceneService] 📦 Metadata update:', {
            sceneId,
            updatedFields: Object.keys(updates).filter(k => metadataFields.includes(k)),
            newMetadata: updatedMetadata
          });
        }
        
        console.log('[SceneService] Updating cloud scene:', sceneId, 'payload:', updatePayload);
        
        // Check if this is a pending_upload scene
        const localScene = await db.scenes.get(sceneId);
        
        if (localScene && localScene.status === 'pending_upload') {
          // For pending_upload scenes, update IndexedDB (they're not in PostgreSQL yet)
          console.log('[SceneService] 🔍 Updating pending_upload scene in IndexedDB:', sceneId);
          await db.scenes.update(sceneId, updates);
          console.log('[SceneService] ✅ Pending_upload scene updated in IndexedDB');
        } else {
          // For confirmed scenes, update PostgreSQL
          await projectsManager.updateScene(Number(sceneId), updatePayload);
          console.log('[SceneService] ✅ Cloud scene updated successfully in PostgreSQL');
        }
        
        logger.info('[SceneService] Cloud scene updated:', sceneId);
      } catch (error) {
        console.error('[SceneService] ❌ Failed to update cloud scene:', error);
        logger.error('[SceneService] Failed to update cloud scene:', error);
        throw error;
      }
    } else {
      // For local projects, update in IndexedDB
      console.log('[SceneService] Using IndexedDB for local project');
      await db.scenes.update(sceneId, updates);
      logger.info('[SceneService] Local scene updated in IndexedDB:', sceneId);
    }
  }
  
  static async deleteScene(sceneId: string): Promise<void> {
    const scene = await db.scenes.get(sceneId);
    if (scene && scene.videoChunks.length > 0) {
      logger.warn('[SceneService] Deleting scene with video chunks:', sceneId);
    }
    
    await db.scenes.delete(sceneId);
    logger.info('[SceneService] Scene deleted:', sceneId);
  }
  
  static async updateSceneStatus(sceneId: string, status: SceneStatus, projectId?: string): Promise<void> {
    // Check if this is a cloud project
    const isCloudProject = projectId && !isNaN(Number(projectId));
    
    if (isCloudProject) {
      // For cloud projects, update PostgreSQL
      // IMPORTANT: When setting status to "pending" for re-record, preserve existing videoUrl/thumbnailUrl
      // They will be deleted later when the new recording is accepted
      try {
        const { projectsManager } = await import('./projectsManager');
        // Backend accepts status in PUT /scenes/:id even though TypeScript types don't include it
        await projectsManager.updateScene(Number(sceneId), { status } as any);
        logger.info('[SceneService] Cloud scene status updated:', sceneId, status);
      } catch (error) {
        logger.error('[SceneService] Failed to update cloud scene status:', error);
        throw error;
      }
    } else {
      // For local projects, update IndexedDB
      await db.scenes.update(sceneId, { status });
      logger.info('[SceneService] Local scene status updated:', sceneId);
    }
  }
  
  static async addSceneChunks(sceneId: string, chunkIds: string[], projectId?: string): Promise<void> {
    console.log('🎬 [SceneService.addSceneChunks] Called with:', {
      sceneId,
      projectId,
      chunkIds,
      chunkCount: chunkIds.length
    });
    
    // Check if this is a cloud project
    const isCloudProject = projectId && !isNaN(Number(projectId));
    
    if (isCloudProject) {
      console.log('☁️ [SceneService.addSceneChunks] Cloud project detected - uploading to S3');
      
      // For cloud projects, upload chunks to S3 and update PostgreSQL
      try {
        await this.uploadSceneChunksToS3(projectId!, sceneId, chunkIds);
        console.log('✅ [SceneService.addSceneChunks] Cloud scene chunks uploaded to S3!', sceneId);
        return;
      } catch (error) {
        console.error('❌ [SceneService.addSceneChunks] Failed to upload to S3:', error);
        throw error;
      }
    }
    
    // For local projects, use IndexedDB
    const scene = await db.scenes.get(sceneId);
    
    console.log('🎬 [SceneService.addSceneChunks] Scene from DB:', {
      found: !!scene,
      sceneId: scene?.id,
      title: scene?.title,
      status: scene?.status,
      existingChunks: scene?.videoChunks?.length || 0
    });
    
    if (!scene) {
      console.error('❌ [SceneService.addSceneChunks] Scene NOT FOUND in database!', sceneId);
      throw new Error(`Scene not found: ${sceneId}`);
    }
    
    const updatedChunks = [...scene.videoChunks, ...chunkIds];
    console.log('🎬 [SceneService.addSceneChunks] Updating scene with chunks:', {
      sceneId,
      oldChunkCount: scene.videoChunks.length,
      newChunkCount: updatedChunks.length,
      addedChunks: chunkIds
    });
    
    await db.scenes.update(sceneId, { videoChunks: updatedChunks });
    
    console.log('✅ [SceneService.addSceneChunks] Scene chunks successfully added!', sceneId);
    logger.info('[SceneService] Scene chunks added:', sceneId);
  }
  
  static async setSceneThumbnail(sceneId: string, thumbnail: string): Promise<void> {
    await db.scenes.update(sceneId, { thumbnail });
    logger.info('[SceneService] Scene thumbnail set:', sceneId);
  }

  /**
   * Upload scene chunks to S3 using videoGuid
   * Helper function that fetches chunks by videoGuid and calls uploadSceneChunksToS3
   */
  static async uploadSceneByVideoGuid(projectId: string, sceneId: string, videoGuid: string): Promise<void> {
    console.log('☁️ [SceneService.uploadSceneByVideoGuid] Fetching chunks for videoGuid:', {
      projectId,
      sceneId,
      videoGuid
    });

    // Fetch all chunks for this videoGuid
    const chunks = await db.chunks
      .where('videoGuid')
      .equals(videoGuid)
      .sortBy('chunkNo');

    if (chunks.length === 0) {
      throw new Error(`No chunks found for videoGuid: ${videoGuid}`);
    }

    const chunkIds = chunks.map(c => c.id);
    console.log('☁️ [SceneService.uploadSceneByVideoGuid] Found chunks:', {
      videoGuid,
      chunkCount: chunks.length,
      chunkIds
    });

    // Call the main upload function
    await this.uploadSceneChunksToS3(projectId, sceneId, chunkIds);
  }

  /**
   * Upload scene chunks to S3 for cloud projects
   * Combines chunks from IndexedDB, uploads to S3, and updates PostgreSQL
   */
  static async uploadSceneChunksToS3(projectId: string, sceneId: string, chunkIds: string[]): Promise<void> {
    console.log('☁️ [SceneService.uploadSceneChunksToS3] Starting upload:', {
      projectId,
      sceneId,
      chunkCount: chunkIds.length
    });

    try {
      // 1. Fetch chunks from IndexedDB and combine into single blob
      const chunkBlobs: Blob[] = [];
      for (const chunkId of chunkIds) {
        const chunk = await db.chunks.get(chunkId);
        if (!chunk) {
          console.error('❌ [uploadSceneChunksToS3] Missing chunk:', chunkId);
          continue;
        }
        chunkBlobs.push(new Blob([chunk.blob]));
      }

      if (chunkBlobs.length === 0) {
        throw new Error('No valid chunks found to upload');
      }

      const videoBlob = new Blob(chunkBlobs, { type: 'video/webm' });
      console.log('☁️ [uploadSceneChunksToS3] Combined video blob:', {
        size: videoBlob.size,
        type: videoBlob.type
      });

      // 2. Get presigned upload URL from API
      const response = await fetch('/api/objects/upload-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileName: `scene-${sceneId}.webm`,
          contentType: 'video/webm',
          projectId: Number(projectId),
          sceneId: Number(sceneId)
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to get upload URL: ${response.status}`);
      }

      const { uploadUrl, fileUrl } = await response.json();
      console.log('☁️ [uploadSceneChunksToS3] Got presigned URL:', { 
        fileUrl,
        uploadUrlLength: uploadUrl.length,
        uploadUrlPreview: uploadUrl.substring(0, 150) + '...'
      });

      // 3. Upload video blob to S3
      console.log('☁️ [uploadSceneChunksToS3] Starting S3 PUT request:', {
        method: 'PUT',
        blobSize: videoBlob.size,
        blobType: videoBlob.type,
        headers: { 'Content-Type': 'video/webm' }
      });
      
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'video/webm'
        },
        body: videoBlob
      });

      console.log('☁️ [uploadSceneChunksToS3] S3 Response received:', {
        status: uploadResponse.status,
        statusText: uploadResponse.statusText,
        ok: uploadResponse.ok,
        headers: Object.fromEntries(uploadResponse.headers.entries())
      });

      if (!uploadResponse.ok) {
        // Get detailed error response from S3
        let errorBody = '';
        try {
          errorBody = await uploadResponse.text();
          console.error('❌ [uploadSceneChunksToS3] S3 Error Response Body:', errorBody);
        } catch (e) {
          console.error('❌ [uploadSceneChunksToS3] Could not read error body:', e);
        }
        
        throw new Error(`S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText}. Error: ${errorBody.substring(0, 200)}`);
      }

      console.log('☁️ [uploadSceneChunksToS3] Upload to S3 successful!');

      // 4. Generate thumbnail from video
      console.log('📸 [uploadSceneChunksToS3] Generating thumbnail from MIDDLE frame (50% into video)...');
      let thumbnailUrl: string | undefined;
      
      try {
        const thumbnailResult = await VideoThumbnailService.extractFrameFromVideo(videoBlob, 'middle', {
          width: 320,
          height: 180,
          quality: 0.85,
          format: 'jpeg'
        });
        
        console.log('📸 [uploadSceneChunksToS3] Thumbnail generated:', {
          size: thumbnailResult.blob.size,
          dimensions: `${thumbnailResult.width}x${thumbnailResult.height}`
        });

        // 5. Get presigned URL for thumbnail upload
        const thumbnailResponse = await fetch('/api/objects/upload-url', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fileName: `scene-${sceneId}-thumbnail.jpg`,
            contentType: 'image/jpeg',
            projectId: Number(projectId),
            sceneId: Number(sceneId)
          })
        });

        if (thumbnailResponse.ok) {
          const { uploadUrl: thumbnailUploadUrl, fileUrl: thumbnailFileUrl } = await thumbnailResponse.json();
          
          // 6. Upload thumbnail to S3
          const thumbnailUploadResponse = await fetch(thumbnailUploadUrl, {
            method: 'PUT',
            headers: {
              'Content-Type': 'image/jpeg'
            },
            body: thumbnailResult.blob
          });

          if (thumbnailUploadResponse.ok) {
            thumbnailUrl = thumbnailFileUrl;
            console.log('✅ [uploadSceneChunksToS3] Thumbnail uploaded to S3:', thumbnailFileUrl);
          } else {
            console.warn('⚠️ [uploadSceneChunksToS3] Thumbnail upload failed, continuing without it');
          }
        }
      } catch (thumbnailError) {
        console.warn('⚠️ [uploadSceneChunksToS3] Failed to generate/upload thumbnail:', thumbnailError);
        // Continue without thumbnail - not critical
      }

      // 7. Calculate actual video duration from metadata
      console.log('⏱️ [uploadSceneChunksToS3] Calculating video duration from metadata...');
      const duration = await this.getVideoDuration(videoBlob);
      console.log('⏱️ [uploadSceneChunksToS3] Video duration:', {
        milliseconds: duration,
        seconds: (duration / 1000).toFixed(2)
      });
      
      // 8. Update scene in PostgreSQL with S3 URL, duration, and thumbnail
      const updateData: any = {
        videoUrl: fileUrl,
        duration
      };
      
      if (thumbnailUrl) {
        updateData.thumbnailUrl = thumbnailUrl;
      }
      
      await projectsManager.updateScene(Number(sceneId), updateData);

      console.log('✅ [uploadSceneChunksToS3] Scene updated in PostgreSQL with S3 URL' + (thumbnailUrl ? ' and thumbnail' : ''));
      logger.info('[SceneService] Scene uploaded to S3 successfully:', {
        projectId,
        sceneId,
        fileUrl,
        thumbnailUrl,
        size: videoBlob.size
      });

    } catch (error) {
      console.error('❌ [uploadSceneChunksToS3] Upload failed:', error);
      logger.error('[SceneService] Failed to upload scene to S3:', error);
      throw error;
    }
  }
  
  // ==================== Project CRUD Operations ====================
  
  static async createProject(projectData: Omit<MultiSceneProject, 'id' | 'createdAt' | 'updatedAt'>): Promise<MultiSceneProject> {
    const project: MultiSceneProject = {
      id: generateId(),
      createdAt: Date.now(),
      updatedAt: Date.now(),
      ...projectData,
    };
    
    await db.projects.add(project);
    
    if (project.scenes.length > 0) {
      for (const scene of project.scenes) {
        await this.createScene(scene);
      }
    }
    
    logger.info('[SceneService] Project created:', project.id);
    return project;
  }
  
  static async getProjectById(projectId: string): Promise<MultiSceneProject | undefined> {
    // Check if this is a cloud project (numeric ID) or local project (UUID string)
    const isCloudProject = !isNaN(Number(projectId));
    
    if (isCloudProject) {
      // Cloud project: Fetch from PostgreSQL via projectsManager
      const numericProjectId = Number(projectId);
      const response = await projectsManager.getProject(numericProjectId);
      const { project, scenes } = response as any;
      
      // Convert cloud data to MultiSceneProject format
      const projectScenes: Scene[] = scenes.map((scene: any) => {
        const sceneObj = {
          id: scene.id.toString(),
          projectId: projectId,
          title: scene.title,
          order: scene.sceneOrder,
          duration: scene.duration || 0,
          videoChunks: [],
          layout: scene.metadata?.layout || [],
          background: scene.metadata?.background || scene.metadata?.layout?.background || { type: 'solid', colors: ['#1F2937'] },
          script: scene.metadata?.script || '',
          tags: scene.tags || [],
          notes: scene.notes || '',
          status: (scene.status || 'pending') as SceneStatus,
          layoutPreset: scene.metadata?.layoutPreset,
          transitionOut: scene.metadata?.transitionOut,
          videoUrl: scene.videoUrl,
          thumbnailUrl: scene.thumbnailUrl,
          micEnabled: scene.micEnabled ?? false, // CRITICAL: Include micEnabled for scene analysis
        };
        
        return sceneObj;
      });
      
      // CRITICAL FIX: Merge pending_upload scenes from IndexedDB (only if they don't exist in PostgreSQL)
      // These scenes have been recorded but not yet confirmed, so they only exist locally
      const pendingScenes = await db.scenes
        .where('projectId').equals(projectId)
        .and(scene => scene.status === 'pending_upload')
        .toArray();
      
      if (pendingScenes.length > 0) {
        console.log(`🔍 [getProjectById] Found ${pendingScenes.length} pending_upload scenes in IndexedDB for project ${projectId}`);
        
        // Only add scenes that don't already exist in projectScenes (deduplication)
        const existingSceneIds = new Set(projectScenes.map(s => s.id));
        const newPendingScenes = pendingScenes.filter(scene => !existingSceneIds.has(scene.id));
        
        if (newPendingScenes.length > 0) {
          console.log(`🔍 [getProjectById] Adding ${newPendingScenes.length} NEW pending_upload scenes from IndexedDB (${pendingScenes.length - newPendingScenes.length} already in PostgreSQL)`);
          projectScenes.push(...newPendingScenes);
          // Re-sort by order
          projectScenes.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        } else {
          console.log(`✅ [getProjectById] All pending_upload scenes already exist in PostgreSQL - skipping IndexedDB merge`);
        }
      }
      
      const multiSceneProject: MultiSceneProject = {
        id: project.id.toString(),
        title: project.name,
        scenes: projectScenes,
        createdAt: new Date(project.createdAt).getTime(),
        updatedAt: new Date(project.updatedAt).getTime(),
        totalDuration: projectScenes.reduce((sum, s) => sum + s.duration, 0),
        status: 'draft' as ProjectStatus,
      };
      
      return multiSceneProject;
    } else {
      // Local project: Read from IndexedDB
      const localProject = await db.projects.get(projectId);
      return localProject;
    }
  }
  
  static async getAllProjects(): Promise<MultiSceneProject[]> {
    return await db.projects.toArray();
  }
  
  static async getProjectsByStatus(status: ProjectStatus): Promise<MultiSceneProject[]> {
    return await db.projects.where('status').equals(status).toArray();
  }
  
  static async getProjectsByTemplateId(templateId: string): Promise<MultiSceneProject[]> {
    return await db.projects.where('templateId').equals(templateId).toArray();
  }
  
  static async updateProject(projectId: string, updates: Partial<MultiSceneProject>): Promise<void> {
    await db.projects.update(projectId, {
      ...updates,
      updatedAt: Date.now(),
    });
    logger.info('[SceneService] Project updated:', projectId);
  }
  
  static async deleteProject(projectId: string): Promise<void> {
    const project = await db.projects.get(projectId);
    if (project) {
      for (const scene of project.scenes) {
        await this.deleteScene(scene.id);
      }
    }
    
    await db.projects.delete(projectId);
    logger.info('[SceneService] Project deleted:', projectId);
  }
  
  static async updateProjectStatus(projectId: string, status: ProjectStatus): Promise<void> {
    await this.updateProject(projectId, { status });
    logger.info('[SceneService] Project status updated:', projectId);
  }
  
  // ==================== Scene Management Operations ====================
  
  static async addSceneToProject(projectId: string, scene: Omit<Scene, 'id'>): Promise<Scene> {
    // Validate projectId is defined and not empty
    if (!projectId) {
      throw new Error('Cannot add scene: projectId is undefined or empty');
    }
    
    // Check if this is a cloud project (numeric ID) or local project (UUID string)
    const isCloudProject = !isNaN(Number(projectId));
    
    console.log('🔍 [SceneService.addSceneToProject] START:', {
      projectId,
      isCloudProject,
      requestedOrder: scene.order,
    });
    
    if (isCloudProject) {
      // Cloud project: Use PostgreSQL via projectsManager
      const numericProjectId = Number(projectId);
      
      // Create scene in cloud database
      const cloudScene = await projectsManager.createScene({
        projectId: numericProjectId,
        sceneOrder: scene.order,
        title: scene.title,
        duration: scene.duration || 0,
        metadata: {
          layout: scene.layout || [],
          background: scene.background,
          script: scene.script || '',
          layoutPreset: scene.layoutPreset,
          transitionOut: scene.transitionOut,
          tags: scene.tags || [],
          notes: scene.notes || '',
        },
      });
      
      console.log('🔍 [SceneService.addSceneToProject] Cloud scene created:', {
        sceneId: cloudScene.id,
        sceneOrder: cloudScene.sceneOrder,
        title: cloudScene.title
      });
      
      // Convert cloud scene to local Scene format
      const newScene: Scene = {
        id: cloudScene.id.toString(),
        title: cloudScene.title,
        order: cloudScene.sceneOrder,
        duration: cloudScene.duration || 0,
        videoChunks: [],
        layout: cloudScene.metadata?.layout || [],
        background: cloudScene.metadata?.background || { type: 'solid', colors: ['#1F2937'] },
        script: cloudScene.metadata?.script || '',
        status: cloudScene.videoUrl ? 'recorded' : 'pending',
        layoutPreset: cloudScene.metadata?.layoutPreset,
        transitionOut: cloudScene.metadata?.transitionOut,
      };
      
      logger.info('[SceneService] Cloud scene added to project:', projectId);
      return newScene;
    } else {
      // Local project: Use IndexedDB
      let project = await db.projects.get(projectId);
      
      console.log('🔍 [SceneService.addSceneToProject] Local project check:', {
        projectId,
        currentScenesCount: project?.scenes.length || 0
      });
      
      // DEFENSIVE: Auto-create project if it doesn't exist
      if (!project) {
        logger.warn(`[SceneService] Project not found in database, auto-creating: ${projectId}`);
        
        const templateId = projectId.startsWith('project-') 
          ? projectId.replace('project-', '') 
          : undefined;
        
        project = {
          id: projectId,
          title: templateId ? `Project from ${templateId}` : 'Untitled Project',
          templateId: templateId,
          scenes: [],
          createdAt: Date.now(),
          updatedAt: Date.now(),
          totalDuration: 0,
          status: 'draft',
        };
        
        try {
          await db.projects.add(project);
          logger.info(`[SceneService] Auto-created missing project: ${projectId}`);
        } catch (error) {
          logger.error(`[SceneService] Failed to auto-create project: ${error}`);
          throw new Error(`Project not found and could not be created: ${projectId}`);
        }
      }
      
      const newScene = await this.createScene({
        ...scene,
        order: project.scenes.length,
      });
      
      console.log('🔍 [SceneService.addSceneToProject] Local scene created:', {
        sceneId: newScene.id,
        finalOrder: newScene.order,
        title: newScene.title
      });
      
      project.scenes.push(newScene);
      await this.updateProject(projectId, { scenes: project.scenes });
      
      logger.info('[SceneService] Local scene added to project:', projectId);
      return newScene;
    }
  }
  
  static async duplicatePreviousScene(projectId: string): Promise<Scene> {
    // Validate projectId is defined and not empty
    if (!projectId) {
      throw new Error('Cannot duplicate scene: projectId is undefined or empty');
    }
    
    // Check if this is a cloud project (numeric ID) or local project (UUID string)
    const isCloudProject = !isNaN(Number(projectId));
    
    if (isCloudProject) {
      // Cloud project: Get from PostgreSQL
      const numericProjectId = Number(projectId);
      const project = await this.getProjectById(projectId);
      
      if (!project) {
        throw new Error(`Project not found: ${projectId}`);
      }
      
      if (project.scenes.length === 0) {
        throw new Error('No previous scene to duplicate');
      }
      
      // Get the last scene (most recently added by order)
      const sortedScenes = [...project.scenes].sort((a, b) => (b.order ?? 0) - (a.order ?? 0));
      const sourceScene = sortedScenes[0];
      
      // Create duplicated scene via cloud API
      const duplicatedScene = await this.addSceneToProject(projectId, {
        order: project.scenes.length,
        title: `${sourceScene.title} (Copy)`,
        duration: 0,
        videoChunks: [],
        layout: sourceScene.layout ? [...sourceScene.layout] : [],
        background: sourceScene.background ? { ...sourceScene.background } : { type: 'solid', colors: ['#1F2937'] },
        script: sourceScene.script || '',
        status: 'pending' as const,
        transitionOut: sourceScene.transitionOut || getDefaultTransition('cut'),
        tags: sourceScene.tags || [],
        notes: sourceScene.notes || '',
        layoutPreset: sourceScene.layoutPreset,
      });
      
      logger.info(`[SceneService] Duplicated cloud scene from ${sourceScene.id} to ${duplicatedScene.id}`);
      return duplicatedScene;
    } else {
      // Local project: Use IndexedDB
      const project = await db.projects.get(projectId);
      if (!project) {
        throw new Error(`Project not found: ${projectId}`);
      }
      
      if (project.scenes.length === 0) {
        throw new Error('No previous scene to duplicate');
      }
      
      // Get the last scene (most recently added)
      const sourceScene = project.scenes[project.scenes.length - 1];
      const freshSourceScene = await db.scenes.get(sourceScene.id);
      
      if (!freshSourceScene) {
        throw new Error(`Source scene not found: ${sourceScene.id}`);
      }
      
      // Create new scene copying layout, background, and duration
      // But clearing script, videoChunks, and thumbnail for fresh recording
      const duplicatedScene = await this.createScene({
        title: `${freshSourceScene.title} (Copy)`,
        order: project.scenes.length,
        layout: [...freshSourceScene.layout], // Deep copy layout array
        background: { ...freshSourceScene.background }, // Copy background config
        script: '', // Clear script for new recording
        duration: freshSourceScene.duration, // Copy duration
        videoChunks: [], // Clear video chunks
        thumbnail: '', // Clear thumbnail
        status: 'pending', // Reset to pending
        layoutPreset: freshSourceScene.layoutPreset, // Copy layout preset name if exists
        transitionOut: freshSourceScene.transitionOut || getDefaultTransition('cut'), // Preserve or use default transition
      });
      
      project.scenes.push(duplicatedScene);
      await this.updateProject(projectId, { scenes: project.scenes });
      
      logger.info(`[SceneService] Duplicated local scene from ${sourceScene.id} to ${duplicatedScene.id}`);
      return duplicatedScene;
    }
  }
  
  static async removeSceneFromProject(projectId: string, sceneId: string): Promise<void> {
    const project = await db.projects.get(projectId);
    if (!project) {
      throw new Error(`Project not found: ${projectId}`);
    }
    
    const sceneIndex = project.scenes.findIndex(s => s.id === sceneId);
    if (sceneIndex === -1) {
      throw new Error(`Scene not found in project: ${sceneId}`);
    }
    
    project.scenes.splice(sceneIndex, 1);
    
    project.scenes.forEach((scene, index) => {
      scene.order = index;
    });
    
    await this.updateProject(projectId, { scenes: project.scenes });
    await this.deleteScene(sceneId);
    
    logger.info('[SceneService] Scene removed from project:', projectId);
  }
  
  static async reorderScenes(projectId: string, sceneIds: string[]): Promise<void> {
    const project = await db.projects.get(projectId);
    if (!project) {
      throw new Error(`Project not found: ${projectId}`);
    }
    
    const reorderedScenes: Scene[] = [];
    sceneIds.forEach((sceneId, index) => {
      const scene = project.scenes.find(s => s.id === sceneId);
      if (scene) {
        scene.order = index;
        reorderedScenes.push(scene);
      }
    });
    
    if (reorderedScenes.length !== project.scenes.length) {
      throw new Error('Scene count mismatch during reorder');
    }
    
    await this.updateProject(projectId, { scenes: reorderedScenes });
    
    for (const scene of reorderedScenes) {
      await db.scenes.update(scene.id, { order: scene.order });
    }
    
    logger.info('[SceneService] Scenes reordered in project:', projectId);
  }
  
  static async updateSceneDuration(projectId: string, sceneId: string, duration: number): Promise<void> {
    // Check if this is a cloud project
    const isCloudProject = !isNaN(Number(projectId));
    
    if (isCloudProject) {
      // For cloud projects, update scene in PostgreSQL (already done in updateScene)
      logger.info('[SceneService] Cloud scene duration already updated via updateScene');
      return;
    }
    
    // For local projects, update in IndexedDB
    const project = await db.projects.get(projectId);
    if (!project) {
      throw new Error(`Project not found: ${projectId}`);
    }
    
    const scene = project.scenes.find(s => s.id === sceneId);
    if (!scene) {
      throw new Error(`Scene not found: ${sceneId}`);
    }
    
    scene.duration = duration;
    const totalDuration = project.scenes.reduce((sum, s) => sum + s.duration, 0);
    
    await this.updateProject(projectId, { 
      scenes: project.scenes,
      totalDuration 
    });
    
    await db.scenes.update(sceneId, { duration });
    
    logger.info('[SceneService] Scene duration updated:', sceneId);
  }

  /**
   * Fix scenes with missing durations by calculating from video chunks
   * This is a utility function to retroactively fix scenes recorded before duration tracking was implemented
   */
  static async fixSceneDurations(projectId: string): Promise<{ fixed: number; skipped: number; failed: number }> {
    console.log('[SceneService] 🔧 Starting to fix scene durations for project:', projectId);
    
    const project = await db.projects.get(projectId);
    if (!project) {
      throw new Error(`Project not found: ${projectId}`);
    }

    const recordedScenes = project.scenes.filter(s => s.status === 'recorded');
    console.log(`[SceneService] Found ${recordedScenes.length} recorded scenes to check`);

    let fixed = 0;
    let skipped = 0;
    let failed = 0;

    for (const scene of recordedScenes) {
      console.log(`[SceneService] Checking scene "${scene.title}":`, {
        duration: scene.duration,
        videoChunksCount: scene.videoChunks?.length || 0,
        status: scene.status
      });

      if (scene.duration > 0) {
        console.log(`[SceneService] Scene "${scene.title}" already has duration: ${scene.duration}ms, skipping`);
        skipped++;
        continue;
      }

      console.log(`[SceneService] Calculating duration for scene "${scene.title}"...`);
      
      try {
        // Get all chunks for this scene
        if (!scene.videoChunks || scene.videoChunks.length === 0) {
          console.warn(`[SceneService] No videoChunks array for scene "${scene.title}", skipping`);
          skipped++;
          continue;
        }

        console.log(`[SceneService] Scene "${scene.title}" has ${scene.videoChunks.length} chunk IDs:`, scene.videoChunks);

        // Build blob from chunks - handle both ArrayBuffer (v5) and Blob formats
        const chunks: Blob[] = [];
        let foundChunks = 0;
        let missingChunks = 0;
        
        for (const chunkId of scene.videoChunks) {
          const chunk = await db.chunks.get(chunkId);
          
          if (!chunk) {
            console.warn(`[SceneService] Chunk not found in database: ${chunkId}`);
            missingChunks++;
            continue;
          }
          
          if (!chunk.blob) {
            console.warn(`[SceneService] Chunk has no blob data: ${chunkId}`);
            missingChunks++;
            continue;
          }

          // Handle both ArrayBuffer (v5 schema) and Blob (older schema)
          if (chunk.blob instanceof ArrayBuffer) {
            chunks.push(new Blob([chunk.blob]));
            foundChunks++;
          } else if (chunk.blob instanceof Blob) {
            chunks.push(chunk.blob);
            foundChunks++;
          } else {
            // Try to convert whatever it is
            chunks.push(new Blob([chunk.blob]));
            foundChunks++;
          }
        }

        console.log(`[SceneService] Chunk retrieval for "${scene.title}": ${foundChunks} found, ${missingChunks} missing`);

        if (chunks.length === 0) {
          console.warn(`[SceneService] No valid chunks for scene "${scene.title}", skipping`);
          skipped++;
          continue;
        }

        const videoBlob = new Blob(chunks, { type: 'video/webm' });
        console.log(`[SceneService] Created blob for "${scene.title}": ${(videoBlob.size / 1024 / 1024).toFixed(2)}MB`);

        // Get duration from video metadata
        const duration = await this.getVideoDuration(videoBlob);
        console.log(`[SceneService] ✓ Calculated duration for "${scene.title}": ${duration}ms (${(duration / 1000).toFixed(2)}s)`);

        // Update the duration
        await this.updateSceneDuration(projectId, scene.id, duration);
        console.log(`[SceneService] ✓ Updated duration in database for "${scene.title}"`);
        fixed++;
        
      } catch (error) {
        console.error(`[SceneService] Failed to fix duration for scene "${scene.title}":`, error);
        failed++;
      }
    }

    console.log(`[SceneService] ✅ Finished fixing scene durations: ${fixed} fixed, ${skipped} skipped, ${failed} failed`);
    return { fixed, skipped, failed };
  }

  /**
   * Get video duration from blob by loading it in a video element
   */
  static async getVideoDuration(videoBlob: Blob): Promise<number> {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      const url = URL.createObjectURL(videoBlob);
      
      let resolved = false;
      
      video.onloadedmetadata = () => {
        if (!resolved) {
          resolved = true;
          const durationMs = Math.floor(video.duration * 1000);
          URL.revokeObjectURL(url);
          resolve(durationMs);
        }
      };
      
      video.onerror = (e) => {
        if (!resolved) {
          resolved = true;
          URL.revokeObjectURL(url);
          reject(new Error('Failed to load video metadata'));
        }
      };
      
      // Timeout after 10 seconds
      setTimeout(() => {
        if (!resolved) {
          resolved = true;
          URL.revokeObjectURL(url);
          reject(new Error('Timeout loading video metadata'));
        }
      }, 10000);
      
      video.src = url;
      video.load();
    });
  }
  
  // ==================== Validation & Utility ====================
  
  static async validateSceneChunkMapping(sceneId: string): Promise<boolean> {
    const scene = await db.scenes.get(sceneId);
    if (!scene) {
      logger.error(`[SceneService] Scene not found: ${sceneId}`);
      return false;
    }
    
    logger.info(`[SceneService] Validating ${scene.videoChunks.length} chunks for scene: ${scene.title}`);
    
    const missingChunks: string[] = [];
    for (const chunkId of scene.videoChunks) {
      const chunk = await db.chunks.get(chunkId);
      if (!chunk) {
        missingChunks.push(chunkId);
      }
    }
    
    if (missingChunks.length > 0) {
      logger.error(`[SceneService] Missing ${missingChunks.length} chunks for scene: ${scene.title}`, {
        sceneId,
        totalChunks: scene.videoChunks.length,
        missingChunks,
        expectedChunks: scene.videoChunks
      });
      return false;
    }
    
    logger.info(`[SceneService] ✅ All chunks validated for scene: ${scene.title}`);
    return true;
  }
  
  static async getProjectCompletionStatus(projectId: string): Promise<{
    totalScenes: number;
    recordedScenes: number;
    pendingScenes: number;
    failedScenes: number;
    percentComplete: number;
  }> {
    const project = await db.projects.get(projectId);
    if (!project) {
      throw new Error(`Project not found: ${projectId}`);
    }
    
    const totalScenes = project.scenes.length;
    const recordedScenes = project.scenes.filter(s => s.status === 'recorded').length;
    const pendingScenes = project.scenes.filter(s => s.status === 'pending').length;
    const failedScenes = project.scenes.filter(s => s.status === 'failed').length;
    const percentComplete = totalScenes > 0 ? (recordedScenes / totalScenes) * 100 : 0;
    
    return {
      totalScenes,
      recordedScenes,
      pendingScenes,
      failedScenes,
      percentComplete: Math.round(percentComplete),
    };
  }
  
  static async getSceneChunkMapping(projectId: string): Promise<SceneChunkMapping[]> {
    const project = await db.projects.get(projectId);
    if (!project) {
      throw new Error(`Project not found: ${projectId}`);
    }
    
    const mappings: SceneChunkMapping[] = [];
    
    // Fetch fresh scene data from db.scenes table (not stale project.scenes)
    // ONLY include scenes with status='recorded'
    for (const projectScene of project.scenes) {
      const scene = await db.scenes.get(projectScene.id);
      
      // Filter: only include recorded scenes with chunks
      if (scene && scene.status === 'recorded' && scene.videoChunks.length > 0 && scene.videoChunks[0]) {
        const firstChunk = await db.chunks.get(scene.videoChunks[0]);
        if (firstChunk) {
          logger.info(`[SceneService] Including scene in mapping: ${scene.title} (${scene.videoChunks.length} chunks)`);
          mappings.push({
            sceneId: scene.id,
            videoGuid: firstChunk.videoGuid,
            chunkIds: scene.videoChunks,
            order: scene.order,
          });
        }
      } else if (scene) {
        logger.debug(`[SceneService] Excluding scene from mapping: ${scene.title} (status=${scene.status}, chunks=${scene.videoChunks.length})`);
      }
    }
    
    logger.info(`[SceneService] getSceneChunkMapping: Found ${mappings.length} recorded scenes with chunks for project ${projectId}`);
    return mappings.sort((a, b) => a.order - b.order);
  }
  
  static validateScene(scene: Scene): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (!scene.id) errors.push('Scene ID is required');
    if (scene.order < 0) errors.push('Scene order must be >= 0');
    if (!scene.title || scene.title.trim().length === 0) errors.push('Scene title is required');
    if (scene.duration < 0) errors.push('Scene duration must be >= 0');
    if (!scene.layout || scene.layout.length === 0) errors.push('Scene layout is required');
    if (!scene.background) errors.push('Scene background is required');
    if (!['pending', 'recording', 'recorded', 'failed'].includes(scene.status)) {
      errors.push('Invalid scene status');
    }
    
    return {
      valid: errors.length === 0,
      errors,
    };
  }
  
  static validateProject(project: MultiSceneProject): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (!project.id) errors.push('Project ID is required');
    if (!project.title || project.title.trim().length === 0) errors.push('Project title is required');
    if (!project.scenes || project.scenes.length === 0) errors.push('Project must have at least one scene');
    if (!['draft', 'in-progress', 'completed'].includes(project.status)) {
      errors.push('Invalid project status');
    }
    
    project.scenes.forEach((scene, index) => {
      const sceneValidation = this.validateScene(scene);
      if (!sceneValidation.valid) {
        errors.push(`Scene ${index + 1} errors: ${sceneValidation.errors.join(', ')}`);
      }
    });
    
    const orderSet = new Set(project.scenes.map(s => s.order));
    if (orderSet.size !== project.scenes.length) {
      errors.push('Duplicate scene order values found');
    }
    
    return {
      valid: errors.length === 0,
      errors,
    };
  }

  // ==================== Scene Preview Operations ====================
  
  /**
   * Get video blob directly by videoGuid (most up-to-date for retakes)
   * @param videoGuid - Video GUID to fetch chunks for
   * @returns Video blob or null if no chunks available
   */
  static async getVideoBlobByGuid(videoGuid: string): Promise<Blob | null> {
    try {
      const chunks = await db.chunks
        .where('videoGuid')
        .equals(videoGuid)
        .sortBy('chunkNo');

      if (chunks.length === 0) {
        logger.warn(`[SceneService] No chunks found for videoGuid: ${videoGuid}`);
        return null;
      }

      const blobs = chunks.map(chunk => chunk.blob);
      const videoBlob = new Blob(blobs, { type: 'video/webm' });
      
      logger.info(`[SceneService] Reconstructed video from ${chunks.length} chunks (${videoBlob.size} bytes)`);
      return videoBlob;
    } catch (error) {
      logger.error('[SceneService] Failed to get video blob by guid:', error);
      return null;
    }
  }
  
  /**
   * Reconstruct video blob from scene chunks for preview
   * Supports both local chunks (IndexedDB) and cloud storage (S3)
   * @param sceneId - Scene ID to get video for
   * @returns Video blob or null if no video data available
   */
  static async getSceneVideoBlob(sceneId: string, projectId?: string): Promise<Blob | null> {
    // Check if this is a cloud project
    const isCloudProject = projectId && !isNaN(Number(projectId));
    
    if (isCloudProject) {
      console.log('☁️ [SceneService.getSceneVideoBlob] Cloud project - fetching from PostgreSQL');
      
      // For cloud projects, fetch scene from PostgreSQL via API
      try {
        const { projectsManager } = await import('./projectsManager');
        const numericProjectId = Number(projectId);
        const numericSceneId = Number(sceneId);
        
        const projectData: any = await projectsManager.getProject(numericProjectId);
        const scene = projectData.scenes?.find((s: any) => s.id === numericSceneId);
        
        if (!scene) {
          logger.warn(`[SceneService] Cloud scene not found: ${sceneId}`);
          return null;
        }
        
        // Try to fetch from S3 if videoUrl exists
        if (scene.videoUrl) {
          console.log('☁️ [SceneService.getSceneVideoBlob] Getting presigned download URL for:', scene.videoUrl);
          
          // Get presigned download URL from backend
          const urlResponse = await fetch('/api/objects/download-url', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ fileUrl: scene.videoUrl })
          });
          
          if (!urlResponse.ok) {
            throw new Error(`Failed to get download URL: ${urlResponse.status}`);
          }
          
          const { downloadUrl } = await urlResponse.json();
          console.log('☁️ [SceneService.getSceneVideoBlob] Got presigned download URL, fetching video...');
          
          const response = await fetch(downloadUrl);
          
          if (!response.ok) {
            throw new Error(`Failed to fetch video: ${response.status}`);
          }
          
          const blob = await response.blob();
          logger.info(`[SceneService] Fetched cloud video blob: ${blob.size} bytes`);
          return blob;
        }
        
        logger.warn(`[SceneService] Cloud scene has no videoUrl: ${sceneId}`);
        return null;
      } catch (error) {
        logger.error(`[SceneService] Failed to fetch cloud scene video:`, error);
        return null;
      }
    }
    
    // For local projects, use IndexedDB
    const scene = await db.scenes.get(sceneId);
    if (!scene) {
      logger.warn(`[SceneService] Scene not found: ${sceneId}`);
      return null;
    }

    // First, try to fetch from cloud storage if videoUrl exists
    if (scene.videoUrl) {
      try {
        logger.info(`[SceneService] Fetching video from cloud storage: ${scene.videoUrl}`);
        const response = await fetch(scene.videoUrl);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch video: ${response.status} ${response.statusText}`);
        }
        
        const blob = await response.blob();
        logger.info(`[SceneService] Fetched video blob from cloud: ${blob.size} bytes`);
        return blob;
      } catch (error) {
        logger.error(`[SceneService] Failed to fetch video from cloud storage:`, error);
        // Fall through to try local chunks
      }
    }

    // Fallback to local chunks if no videoUrl or cloud fetch failed
    if (scene.videoChunks.length === 0) {
      logger.warn(`[SceneService] No video data available for scene: ${sceneId} (no chunks and no videoUrl)`);
      return null;
    }

    try {
      const chunkBlobs: Blob[] = [];
      
      for (const chunkId of scene.videoChunks) {
        const chunk = await db.chunks.get(chunkId);
        if (!chunk) {
          logger.error(`[SceneService] Missing chunk ${chunkId} for scene: ${sceneId}`);
          continue;
        }
        
        chunkBlobs.push(new Blob([chunk.blob]));
      }

      if (chunkBlobs.length === 0) {
        logger.error(`[SceneService] No valid chunks found for scene: ${sceneId}`);
        return null;
      }

      const videoBlob = new Blob(chunkBlobs, { type: 'video/webm' });
      logger.info(`[SceneService] Reconstructed video blob from local chunks for scene ${sceneId}: ${videoBlob.size} bytes`);
      return videoBlob;
    } catch (error) {
      logger.error(`[SceneService] Failed to reconstruct video blob: ${error}`);
      return null;
    }
  }

  /**
   * Delete all chunks associated with a scene and reset status
   * Used for re-recording a scene
   */
  static async deleteSceneChunks(sceneId: string): Promise<void> {
    const scene = await db.scenes.get(sceneId);
    if (!scene) {
      throw new Error(`Scene not found: ${sceneId}`);
    }

    try {
      for (const chunkId of scene.videoChunks) {
        await db.chunks.delete(chunkId);
      }

      await db.scenes.update(sceneId, {
        videoChunks: [],
        status: 'pending',
        duration: 0,
      });

      logger.info(`[SceneService] Deleted ${scene.videoChunks.length} chunks for scene: ${sceneId}`);
    } catch (error) {
      logger.error(`[SceneService] Failed to delete scene chunks: ${error}`);
      throw error;
    }
  }

  /**
   * Trim a scene video to specified start and end times
   * Creates a new trimmed version and replaces the original chunks
   */
  static async trimScene(sceneId: string, trimStart: number, trimEnd: number): Promise<void> {
    const scene = await db.scenes.get(sceneId);
    if (!scene) {
      throw new Error(`Scene not found: ${sceneId}`);
    }

    logger.info('[SceneService] Trimming scene:', { sceneId, trimStart, trimEnd });

    try {
      // Get original video blob
      const originalBlob = await this.getSceneVideoBlob(sceneId);
      if (!originalBlob) {
        throw new Error('Failed to load original video');
      }

      // Create video element to extract trimmed portion
      const video = document.createElement('video');
      const url = URL.createObjectURL(originalBlob);
      video.src = url;
      video.muted = true;

      await new Promise((resolve, reject) => {
        video.onloadedmetadata = resolve;
        video.onerror = reject;
      });

      // Create canvas to capture frames
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Failed to create canvas context');
      }

      // Capture trimmed video using MediaStream
      // Use optimized codec and bitrate settings from browserCompatibility
      // @see Base docs/Vimeo Recording Architecture Analysis.md
      const stream = canvas.captureStream(30); // 30 FPS
      const mimeType = getSupportedMimeType();
      const videoBitrate = BITRATES_BY_QUALITY["1080p"]; // Use 1080p bitrate for trimmed video
      
      const recorderOptions: MediaRecorderOptions = {
        videoBitsPerSecond: videoBitrate,
        audioBitsPerSecond: AUDIO_BITRATE
      };
      
      // Only set mimeType if we have a valid one (guard for unsupported browsers)
      if (mimeType) {
        recorderOptions.mimeType = mimeType;
      }
      
      const mediaRecorder = new MediaRecorder(stream, recorderOptions);

      const chunks: Blob[] = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      // Start recording and playback
      return new Promise((resolve, reject) => {
        mediaRecorder.onstart = () => {
          video.currentTime = trimStart;
          video.play();
        };

        mediaRecorder.onstop = async () => {
          URL.revokeObjectURL(url);

          if (chunks.length === 0) {
            reject(new Error('No video data captured'));
            return;
          }

          try {
            // Create trimmed blob
            const trimmedBlob = new Blob(chunks, { type: 'video/webm' });
            const trimmedDuration = Math.round((trimEnd - trimStart) * 1000);

            // Delete old chunks
            for (const chunkId of scene.videoChunks) {
              await db.chunks.delete(chunkId);
            }

            // Create new chunk for trimmed video
            const newChunkId = generateId();
            await db.chunks.add({
              id: newChunkId,
              videoGuid: scene.id,
              chunkNo: 0,
              blob: trimmedBlob,
              status: 'done',
              isLast: true,
              size: trimmedBlob.size,
              retry: 0,
              createdAt: Date.now(),
              updatedAt: Date.now(),
            });

            // Update scene with new chunk and duration
            await db.scenes.update(sceneId, {
              videoChunks: [newChunkId],
              duration: trimmedDuration,
            });

            logger.info('[SceneService] Scene trimmed successfully:', {
              sceneId,
              newDuration: trimmedDuration,
              originalSize: originalBlob.size,
              trimmedSize: trimmedBlob.size
            });

            resolve();
          } catch (err) {
            reject(err);
          }
        };

        mediaRecorder.onerror = (e) => {
          URL.revokeObjectURL(url);
          reject(new Error('MediaRecorder error'));
        };

        // Draw frames to canvas during playback
        const drawFrame = () => {
          if (video.currentTime >= trimEnd) {
            video.pause();
            mediaRecorder.stop();
            return;
          }

          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          requestAnimationFrame(drawFrame);
        };

        video.ontimeupdate = () => {
          if (video.currentTime >= trimEnd) {
            video.pause();
            mediaRecorder.stop();
          }
        };

        mediaRecorder.start();
        drawFrame();
      });
    } catch (error) {
      logger.error('[SceneService] Failed to trim scene:', error);
      throw error;
    }
  }

  // ==================== Enhanced Metadata Operations (Sprint 19+) ====================

  /**
   * Update scene notes/description
   */
  static async updateSceneNotes(sceneId: string, notes: string): Promise<void> {
    await db.scenes.update(sceneId, { notes });
    logger.info('[SceneService] Scene notes updated:', sceneId);
  }

  /**
   * Update scene tags
   */
  static async updateSceneTags(sceneId: string, tags: SceneTagType[]): Promise<void> {
    await db.scenes.update(sceneId, { tags });
    logger.info('[SceneService] Scene tags updated:', { sceneId, tags });
  }

  /**
   * Toggle scene expanded state
   */
  static async toggleSceneExpanded(sceneId: string): Promise<void> {
    const scene = await db.scenes.get(sceneId);
    if (!scene) {
      throw new Error(`Scene not found: ${sceneId}`);
    }
    await db.scenes.update(sceneId, { isExpanded: !scene.isExpanded });
    logger.info('[SceneService] Scene expanded toggled:', sceneId);
  }

  /**
   * Add a recording take to scene history
   */
  static async addRecordingTake(
    sceneId: string,
    duration: number,
    isKept: boolean,
    note?: string
  ): Promise<void> {
    const scene = await db.scenes.get(sceneId);
    if (!scene) {
      throw new Error(`Scene not found: ${sceneId}`);
    }

    const recordingHistory = scene.recordingHistory || [];
    const newTake = {
      takeNumber: recordingHistory.length + 1,
      timestamp: Date.now(),
      duration,
      isKept,
      note,
    };

    await db.scenes.update(sceneId, {
      recordingHistory: [...recordingHistory, newTake],
    });

    logger.info('[SceneService] Recording take added:', { sceneId, takeNumber: newTake.takeNumber });
  }

  /**
   * Update scene approval status
   */
  static async updateSceneApprovalStatus(
    sceneId: string,
    approvalStatus: 'ready' | 'needs-review' | 'approved'
  ): Promise<void> {
    await db.scenes.update(sceneId, { approvalStatus });
    logger.info('[SceneService] Scene approval status updated:', { sceneId, approvalStatus });
  }

  /**
   * Set scene error message (for failed scenes)
   */
  static async setSceneError(sceneId: string, errorMessage: string): Promise<void> {
    await db.scenes.update(sceneId, {
      status: 'failed',
      errorMessage,
    });
    logger.error('[SceneService] Scene marked as failed:', { sceneId, errorMessage });
  }

  /**
   * Duplicate a scene (copy all settings, metadata, but not recording)
   */
  static async duplicateScene(sceneId: string, order: number): Promise<Scene> {
    const sourceScene = await db.scenes.get(sceneId);
    if (!sourceScene) {
      throw new Error(`Scene not found: ${sceneId}`);
    }

    const duplicatedScene: Scene = {
      ...sourceScene,
      id: generateId(),
      order,
      title: `${sourceScene.title} (Copy)`,
      status: 'pending',
      duration: 0,
      videoChunks: [],
      thumbnail: undefined,
      recordedAt: undefined,
      recordingHistory: [],
      approvalStatus: undefined,
      errorMessage: undefined,
      videoGuid: undefined,
    };

    await db.scenes.add(duplicatedScene);
    logger.info('[SceneService] Scene duplicated:', { from: sourceScene.id, to: duplicatedScene.id });
    return duplicatedScene;
  }

  /**
   * Copy settings from one scene to another (layout, background, script, tags, notes)
   */
  static async copySceneSettings(
    sourceSceneId: string,
    targetSceneId: string,
    copyOptions: {
      copyLayout?: boolean;
      copyBackground?: boolean;
      copyScript?: boolean;
      copyTags?: boolean;
      copyNotes?: boolean;
    } = {}
  ): Promise<void> {
    const sourceScene = await db.scenes.get(sourceSceneId);
    const targetScene = await db.scenes.get(targetSceneId);

    if (!sourceScene || !targetScene) {
      throw new Error('Source or target scene not found');
    }

    const updates: Partial<Scene> = {};

    if (copyOptions.copyLayout !== false) {
      updates.layout = sourceScene.layout;
      updates.layoutPreset = sourceScene.layoutPreset;
    }

    if (copyOptions.copyBackground !== false) {
      updates.background = sourceScene.background;
    }

    if (copyOptions.copyScript !== false) {
      updates.script = sourceScene.script;
    }

    if (copyOptions.copyTags !== false) {
      updates.tags = sourceScene.tags;
    }

    if (copyOptions.copyNotes !== false) {
      updates.notes = sourceScene.notes;
    }

    await db.scenes.update(targetSceneId, updates);
    logger.info('[SceneService] Settings copied:', { from: sourceSceneId, to: targetSceneId });
  }
}

export default SceneService;
